import InvoiceAutomationStatusEnum from '../Enums/InvoiceAutomationStatusEnum';
import InvoiceAutomationRunScheduleOptionsArray from '../Enums/InvoiceAutomationRunScheduleOptionsArray';
import InvoiceAutomationJobMonthRuleOptionsArray from '../Enums/InvoiceAutomationJobMonthRuleOptionsArray';
import ClientInvoiceFormOptionsArray from '../Enums/ClientInvoiceFormOptionsArray';
import ClientInvoiceReportOrderOptionsArray from '../Enums/ClientInvoiceReportOrderOptionsArray';
import ClientInvoiceReportGroupOptionsArray from '../Enums/ClientInvoiceReportGroupOptionsArray';
import selectHelper from './selectHelper';
import { formatMonthLabel } from './invoiceAutomationMonthHelper';
import { formatProjectLabel } from './invoiceAutomationEditorHelper';

// The settings a run ran with, from the JSON it stored (its names are the backend's, starting upper case); null
// when there is none or it cannot be read.
export function parseSettingsSnapshot(settingsSnapshot?: string | null) {
  if (!settingsSnapshot) {
    return null;
  }
  try {
    return JSON.parse(settingsSnapshot);
  } catch (e) {
    return null;
  }
}

// The setup's own settings, as definition card rows, with the labels the setup form shows.
export function getSetupSettingsRows(snapshot) {
  return [
    {
      label: 'Status',
      value: snapshot.IsActive ? InvoiceAutomationStatusEnum.Active : InvoiceAutomationStatusEnum.Deactivated,
    },
    {
      label: 'Runs',
      value: selectHelper.getOptionLabel(InvoiceAutomationRunScheduleOptionsArray, snapshot.RunSchedule),
    },
    {
      label: 'A job belongs to the month',
      value: selectHelper.getOptionLabel(InvoiceAutomationJobMonthRuleOptionsArray, snapshot.JobMonthRule),
    },
    { label: 'First month', value: formatMonthLabel(snapshot.FirstMonth) },
  ];
}

// The other projects that were on the invoice when the run ran, as the run detail names them ({ projectId,
// projectName }), as a definition card row: each "#id name" on a line of its own, or "None", as for a run saved before
// projects could be combined.
export function getOtherProjectsRow(otherProjects?: { projectId: number; projectName?: string | null }[] | null) {
  const otherProjectLabels = (otherProjects ?? []).map((otherProject) =>
    formatProjectLabel(otherProject.projectId, otherProject.projectName)
  );
  if (otherProjectLabels.length === 0) {
    return { label: 'Other projects on the invoice', value: 'None' };
  }
  return { label: 'Other projects on the invoice', value: otherProjectLabels.join('\n'), isLong: true };
}

// An invoice's settings, as definition card rows, without the bill-to contact and the currency, whose names have to
// be looked up.
export function getInvoiceSettingsRows(invoice) {
  return [
    { label: 'Days to pay', value: invoice.DaysToPay },
    { label: 'Late fee %', value: invoice.LatePaymentInterestRate },
    { label: 'Purchase order #', value: invoice.Ponumber },
    {
      label: 'Invoice form',
      value: selectHelper.getOptionLabel(ClientInvoiceFormOptionsArray, invoice.DefaultReportName),
    },
    {
      label: 'Form order',
      value: selectHelper.getOptionLabel(ClientInvoiceReportOrderOptionsArray, invoice.ReportOrder),
    },
    {
      label: 'Form group 1',
      value: selectHelper.getOptionLabel(ClientInvoiceReportGroupOptionsArray, invoice.ReportGroup1),
    },
    {
      label: 'Form group 2',
      value: selectHelper.getOptionLabel(ClientInvoiceReportGroupOptionsArray, invoice.ReportGroup2),
    },
  ];
}

// An invoice's texts, where {Month} and {Year} become the draft's month and year, as stacked definition card rows.
export function getInvoiceTextRows(invoice) {
  return [
    { label: 'Internal reference number', value: invoice.ReferenceTemplate, isLong: true },
    { label: 'Invoice comments', value: invoice.CommentTemplate, isLong: true },
  ];
}
